import React, { useState, useEffect } from 'react';
import {
  Button,
  Form,
  Input,
  Table,
  Space,
  Card,
  message,
  Modal,
  Select,
  Switch,
  Tag,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Tooltip,
  Badge,
  Divider,
  InputNumber
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  ExportOutlined,
  ImportOutlined,
  BarChartOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import 'antd/dist/reset.css';
import API from '../services/api';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;

const AdminKnowledgeBase = () => {
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [knowledgeList, setKnowledgeList] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [statsVisible, setStatsVisible] = useState(false);
  const [stats, setStats] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentDetail, setCurrentDetail] = useState(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importData, setImportData] = useState('');

  // 知识库分类选项
  const categoryOptions = [
    { label: '攻击查询', value: 'attack_query' },
    { label: '流量查询', value: 'traffic_query' },
    { label: '规则管理', value: 'rule_management' },
    { label: '黑白名单', value: 'blackwhite_list' },
    { label: '系统设置', value: 'system_settings' },
    { label: 'IP管理', value: 'ip_management' },
    { label: '工单系统', value: 'ticket_system' },
    { label: '账号管理', value: 'account_management' },
    { label: '其他', value: 'other' }
  ];

  // 参数类型选项
  const paramTypeOptions = [
    { label: '无需参数', value: 'none' },
    { label: 'IP地址', value: 'ip' },
    { label: '时间范围', value: 'time_range' },
    { label: '规则ID', value: 'rule_id' },
    { label: '用户ID', value: 'user_id' },
    { label: '其他', value: 'other' }
  ];

  // API动作选项
  const apiActionOptions = [
    { label: '无操作', value: 'none' },
    { label: '查询数据', value: 'query' },
    { label: '创建记录', value: 'create' },
    { label: '更新记录', value: 'update' },
    { label: '删除记录', value: 'delete' },
    { label: '批量操作', value: 'batch' }
  ];

  // 获取知识库列表
  const fetchKnowledgeList = async (page = currentPage, limit = pageSize, filters = {}) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        ...filters
      };

      const response = await API.get('/api/knowledge-base/admin/list', { params });
      
      if (response.data.success) {
        setKnowledgeList(response.data.data.list);
        setTotal(response.data.data.total);
        setCurrentPage(response.data.data.page);
      } else {
        message.error(response.data.message || '获取知识库列表失败');
      }
    } catch (error) {
      console.error('获取知识库列表错误:', error);
      message.error(error.response?.data?.message || '获取知识库列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取统计信息
  const fetchStats = async () => {
    try {
      const response = await API.get('/api/knowledge-base/admin/stats');
      
      if (response.data.success) {
        setStats(response.data.data);
        setStatsVisible(true);
      } else {
        message.error(response.data.message || '获取统计信息失败');
      }
    } catch (error) {
      console.error('获取统计信息错误:', error);
      message.error(error.response?.data?.message || '获取统计信息失败');
    }
  };

  // 查看详情
  const handleViewDetail = async (id) => {
    try {
      const response = await API.get(`/api/knowledge-base/admin/${id}`);
      
      if (response.data.success) {
        setCurrentDetail(response.data.data);
        setDetailVisible(true);
      } else {
        message.error(response.data.message || '获取详情失败');
      }
    } catch (error) {
      console.error('获取详情错误:', error);
      message.error(error.response?.data?.message || '获取详情失败');
    }
  };

  // 搜索
  const handleSearch = (values) => {
    const filters = {};
    if (values.keyword) filters.keyword = values.keyword;
    if (values.category) filters.category = values.category;
    if (values.isActive !== undefined) filters.isActive = values.isActive;
    
    setCurrentPage(1);
    fetchKnowledgeList(1, pageSize, filters);
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    setCurrentPage(1);
    fetchKnowledgeList(1, pageSize);
  };

  // 打开创建/编辑弹窗
  const handleOpenModal = (record = null) => {
    if (record) {
      setEditingId(record._id);
      form.setFieldsValue({
        ...record,
        keywords: record.keywords?.join(', ') || ''
      });
    } else {
      setEditingId(null);
      form.resetFields();
    }
    setModalVisible(true);
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // 处理关键词
      const keywords = values.keywords 
        ? values.keywords.split(',').map(k => k.trim()).filter(k => k)
        : [];

      const data = {
        ...values,
        keywords
      };

      if (editingId) {
        // 更新
        const response = await API.put(`/api/knowledge-base/admin/${editingId}`, data);
        if (response.data.success) {
          message.success('更新成功');
          setModalVisible(false);
          fetchKnowledgeList();
        } else {
          message.error(response.data.message || '更新失败');
        }
      } else {
        // 创建
        const response = await API.post('/api/knowledge-base/admin/create', data);
        if (response.data.success) {
          message.success('创建成功');
          setModalVisible(false);
          fetchKnowledgeList();
        } else {
          message.error(response.data.message || '创建失败');
        }
      }
    } catch (error) {
      if (error.errorFields) {
        message.error('请填写必填项');
      } else {
        console.error('提交错误:', error);
        message.error(error.response?.data?.message || '操作失败');
      }
    }
  };

  // 删除单个
  const handleDelete = async (id) => {
    try {
      const response = await API.delete(`/api/knowledge-base/admin/${id}`);
      if (response.data.success) {
        message.success('删除成功');
        fetchKnowledgeList();
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除错误:', error);
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的项');
      return;
    }

    try {
      const response = await API.post('/api/knowledge-base/admin/batch-delete', {
        ids: selectedRowKeys
      });
      
      if (response.data.success) {
        message.success(`成功删除 ${response.data.deletedCount} 条记录`);
        setSelectedRowKeys([]);
        fetchKnowledgeList();
      } else {
        message.error(response.data.message || '批量删除失败');
      }
    } catch (error) {
      console.error('批量删除错误:', error);
      message.error(error.response?.data?.message || '批量删除失败');
    }
  };

  // 切换启用状态
  const handleToggleStatus = async (id) => {
    try {
      const response = await API.patch(`/api/knowledge-base/admin/${id}/toggle`);
      if (response.data.success) {
        message.success(response.data.message);
        fetchKnowledgeList();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('切换状态错误:', error);
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  // 导出数据
  const handleExport = () => {
    const exportData = knowledgeList.map(item => ({
      标题: item.title,
      分类: getCategoryLabel(item.category),
      关键词: item.keywords?.join(', ') || '',
      内容: item.content,
      需要参数: item.requiresParams ? '是' : '否',
      参数类型: getParamTypeLabel(item.paramType),
      API动作: getApiActionLabel(item.apiAction),
      优先级: item.priority,
      状态: item.isActive ? '启用' : '禁用',
      使用次数: item.usageCount || 0,
      创建时间: dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss'),
      更新时间: dayjs(item.updatedAt).format('YYYY-MM-DD HH:mm:ss')
    }));

    const jsonStr = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    saveAs(blob, `知识库导出_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.json`);
    message.success('导出成功');
  };

  // 导入数据
  const handleImport = async () => {
    try {
      const knowledgeList = JSON.parse(importData);
      
      if (!Array.isArray(knowledgeList)) {
        message.error('导入数据格式错误，应为数组');
        return;
      }

      const response = await API.post('/api/knowledge-base/admin/import', {
        knowledgeList
      });

      if (response.data.success) {
        message.success(response.data.message);
        setImportModalVisible(false);
        setImportData('');
        fetchKnowledgeList();
      } else {
        message.error(response.data.message || '导入失败');
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        message.error('JSON 格式错误');
      } else {
        console.error('导入错误:', error);
        message.error(error.response?.data?.message || '导入失败');
      }
    }
  };

  // 获取分类标签
  const getCategoryLabel = (value) => {
    const option = categoryOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  // 获取参数类型标签
  const getParamTypeLabel = (value) => {
    const option = paramTypeOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  // 获取API动作标签
  const getApiActionLabel = (value) => {
    const option = apiActionOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  // 表格列定义
  const columns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text}</span>
        </Tooltip>
      )
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category) => (
        <Tag color="blue">{getCategoryLabel(category)}</Tag>
      )
    },
    {
      title: '关键词',
      dataIndex: 'keywords',
      key: 'keywords',
      width: 150,
      ellipsis: true,
      render: (keywords) => (
        <Tooltip title={keywords?.join(', ')}>
          <span>{keywords?.slice(0, 3).join(', ')}{keywords?.length > 3 ? '...' : ''}</span>
        </Tooltip>
      )
    },
    {
      title: '需要参数',
      dataIndex: 'requiresParams',
      key: 'requiresParams',
      width: 100,
      align: 'center',
      render: (requiresParams) => (
        requiresParams ? 
          <Tag color="orange">是</Tag> : 
          <Tag color="default">否</Tag>
      )
    },
    {
      title: '参数类型',
      dataIndex: 'paramType',
      key: 'paramType',
      width: 100,
      render: (paramType) => getParamTypeLabel(paramType)
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      align: 'center',
      sorter: (a, b) => a.priority - b.priority
    },
    {
      title: '使用次数',
      dataIndex: 'usageCount',
      key: 'usageCount',
      width: 100,
      align: 'center',
      sorter: (a, b) => (a.usageCount || 0) - (b.usageCount || 0),
      render: (count) => (
        <Badge count={count || 0} showZero style={{ backgroundColor: '#52c41a' }} />
      )
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      align: 'center',
      render: (isActive, record) => (
        <Popconfirm
          title={`确定要${isActive ? '禁用' : '启用'}吗？`}
          onConfirm={() => handleToggleStatus(record._id)}
          okText="确定"
          cancelText="取消"
        >
          <Switch checked={isActive} />
        </Popconfirm>
      )
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (time) => dayjs(time).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record._id)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleOpenModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除吗？"
            onConfirm={() => handleDelete(record._id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys)
  };

  // 初始加载
  useEffect(() => {
    fetchKnowledgeList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ margin: 0, display: 'inline-block' }}>
            <FileTextOutlined style={{ marginRight: 8 }} />
            知识库管理
          </h2>
          <Button
            type="primary"
            icon={<BarChartOutlined />}
            onClick={fetchStats}
            style={{ float: 'right', marginLeft: 8 }}
          >
            统计信息
          </Button>
        </div>

        <Divider />

        {/* 搜索表单 */}
        <Form form={searchForm} layout="inline" onFinish={handleSearch} style={{ marginBottom: 16 }}>
          <Form.Item name="keyword">
            <Input
              placeholder="搜索标题、关键词或内容"
              style={{ width: 250 }}
              allowClear
            />
          </Form.Item>
          <Form.Item name="category">
            <Select
              placeholder="选择分类"
              style={{ width: 150 }}
              allowClear
            >
              {categoryOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="isActive">
            <Select
              placeholder="状态"
              style={{ width: 120 }}
              allowClear
            >
              <Option value={true}>启用</Option>
              <Option value={false}>禁用</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset} icon={<ReloadOutlined />}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 操作按钮 */}
        <Space style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenModal()}
          >
            新建知识库
          </Button>
          <Popconfirm
            title={`确定要删除选中的 ${selectedRowKeys.length} 项吗？`}
            onConfirm={handleBatchDelete}
            disabled={selectedRowKeys.length === 0}
            okText="确定"
            cancelText="取消"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={selectedRowKeys.length === 0}
            >
              批量删除 {selectedRowKeys.length > 0 && `(${selectedRowKeys.length})`}
            </Button>
          </Popconfirm>
          <Button
            icon={<ExportOutlined />}
            onClick={handleExport}
            disabled={knowledgeList.length === 0}
          >
            导出数据
          </Button>
          <Button
            icon={<ImportOutlined />}
            onClick={() => setImportModalVisible(true)}
          >
            导入数据
          </Button>
        </Space>

        {/* 表格 */}
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={knowledgeList}
          rowKey="_id"
          loading={loading}
          scroll={{ x: 1500 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
              fetchKnowledgeList(page, size);
            }
          }}
        />
      </Card>

      {/* 创建/编辑弹窗 */}
      <Modal
        title={editingId ? '编辑知识库' : '新建知识库'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={800}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            priority: 0,
            isActive: true,
            requiresParams: false,
            paramType: 'none',
            apiAction: 'none'
          }}
        >
          <Form.Item
            label="标题"
            name="title"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input placeholder="请输入知识库标题" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="分类"
                name="category"
                rules={[{ required: true, message: '请选择分类' }]}
              >
                <Select placeholder="请选择分类">
                  {categoryOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="优先级"
                name="priority"
                tooltip="数值越大优先级越高"
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="关键词"
            name="keywords"
            tooltip="多个关键词用逗号分隔"
          >
            <Input placeholder="例如：攻击,查询,日志" />
          </Form.Item>

          <Form.Item
            label="内容"
            name="content"
            rules={[{ required: true, message: '请输入内容' }]}
          >
            <TextArea
              rows={6}
              placeholder="请输入知识库内容"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="需要参数"
                name="requiresParams"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="参数类型"
                name="paramType"
              >
                <Select>
                  {paramTypeOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="API动作"
                name="apiAction"
              >
                <Select>
                  {apiActionOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="启用状态"
            name="isActive"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情弹窗 */}
      <Modal
        title="知识库详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {currentDetail && (
          <div>
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="使用次数"
                    value={currentDetail.usageCount || 0}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="优先级"
                    value={currentDetail.priority}
                    prefix={<BarChartOutlined />}
                  />
                </Col>
              </Row>
            </Card>

            <Card size="small" title="基本信息" style={{ marginBottom: 16 }}>
              <p><strong>标题：</strong>{currentDetail.title}</p>
              <p><strong>分类：</strong><Tag color="blue">{getCategoryLabel(currentDetail.category)}</Tag></p>
              <p>
                <strong>状态：</strong>
                {currentDetail.isActive ? 
                  <Tag color="success" icon={<CheckCircleOutlined />}>启用</Tag> : 
                  <Tag color="error" icon={<CloseCircleOutlined />}>禁用</Tag>
                }
              </p>
              <p>
                <strong>关键词：</strong>
                {currentDetail.keywords?.map(kw => (
                  <Tag key={kw} color="cyan">{kw}</Tag>
                ))}
              </p>
            </Card>

            <Card size="small" title="参数配置" style={{ marginBottom: 16 }}>
              <p><strong>需要参数：</strong>{currentDetail.requiresParams ? '是' : '否'}</p>
              <p><strong>参数类型：</strong>{getParamTypeLabel(currentDetail.paramType)}</p>
              <p><strong>API动作：</strong>{getApiActionLabel(currentDetail.apiAction)}</p>
            </Card>

            <Card size="small" title="内容" style={{ marginBottom: 16 }}>
              <div style={{ 
                whiteSpace: 'pre-wrap', 
                background: '#f5f5f5', 
                padding: 12, 
                borderRadius: 4 
              }}>
                {currentDetail.content}
              </div>
            </Card>

            <Card size="small" title="其他信息">
              <p><strong>创建人：</strong>{currentDetail.createdBy?.username || '-'}</p>
              <p><strong>创建时间：</strong>{dayjs(currentDetail.createdAt).format('YYYY-MM-DD HH:mm:ss')}</p>
              <p><strong>更新人：</strong>{currentDetail.updatedBy?.username || '-'}</p>
              <p><strong>更新时间：</strong>{dayjs(currentDetail.updatedAt).format('YYYY-MM-DD HH:mm:ss')}</p>
            </Card>
          </div>
        )}
      </Modal>

      {/* 统计信息弹窗 */}
      <Modal
        title="知识库统计信息"
        open={statsVisible}
        onCancel={() => setStatsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setStatsVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {stats && (
          <div>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="总数"
                    value={stats.total}
                    prefix={<FileTextOutlined />}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="启用"
                    value={stats.active}
                    valueStyle={{ color: '#3f8600' }}
                    prefix={<CheckCircleOutlined />}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic
                    title="禁用"
                    value={stats.inactive}
                    valueStyle={{ color: '#cf1322' }}
                    prefix={<CloseCircleOutlined />}
                  />
                </Card>
              </Col>
            </Row>

            <Card title="分类统计" style={{ marginBottom: 16 }}>
              {stats.categoryStats?.map(item => (
                <div key={item._id} style={{ marginBottom: 8 }}>
                  <Tag color="blue">{getCategoryLabel(item._id)}</Tag>
                  <span style={{ marginLeft: 8 }}>数量: {item.count}</span>
                </div>
              ))}
            </Card>

            <Card title="使用次数排行 (Top 10)">
              <Table
                dataSource={stats.topUsed}
                rowKey="_id"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: '标题',
                    dataIndex: 'title',
                    key: 'title'
                  },
                  {
                    title: '分类',
                    dataIndex: 'category',
                    key: 'category',
                    render: (category) => (
                      <Tag color="blue">{getCategoryLabel(category)}</Tag>
                    )
                  },
                  {
                    title: '使用次数',
                    dataIndex: 'usageCount',
                    key: 'usageCount',
                    align: 'center',
                    render: (count) => (
                      <Badge count={count || 0} showZero style={{ backgroundColor: '#52c41a' }} />
                    )
                  }
                ]}
              />
            </Card>
          </div>
        )}
      </Modal>

      {/* 导入数据弹窗 */}
      <Modal
        title="导入知识库数据"
        open={importModalVisible}
        onOk={handleImport}
        onCancel={() => {
          setImportModalVisible(false);
          setImportData('');
        }}
        width={800}
        okText="导入"
        cancelText="取消"
      >
        <p>请粘贴 JSON 格式的知识库数据：</p>
        <TextArea
          rows={15}
          value={importData}
          onChange={(e) => setImportData(e.target.value)}
          placeholder={`示例格式：
[
  {
    "title": "如何查询攻击日志",
    "category": "attack_query",
    "keywords": ["攻击", "查询", "日志"],
    "content": "您可以通过以下步骤查询攻击日志...",
    "requiresParams": true,
    "paramType": "ip",
    "apiAction": "query",
    "priority": 10,
    "isActive": true
  }
]`}
        />
      </Modal>
    </div>
  );
};

export default AdminKnowledgeBase;

